import React, { useState, useEffect } from "react";
import { Modal, CloseButton } from "react-bootstrap";
import { listWalletsForKey, generateMcpKey } from "../../../api/mcpApi";
import { truncateAddress } from "../shared";
import { useAuth } from "../../../components/web3/context/AuthContext";
import { toast } from "react-hot-toast";

const MCP_TOOLS = [
  "horus_wallet_info",
  "horus_tx_history",
  "horus_send_native",
  "horus_send_token",
  "horus_pay",
  "horus_check_price",
  "horus_contract_read",
  "horus_contract_write",
  "horus_gas_estimate",
  "horus_resolve_address",
  "horus_token_price",
  "horus_mcp_status",
  "horus_mcp_update",
  "ows_get_wallets",
  "ows_get_balance",
  "ows_sign",
  "ows_sign_message",
  "horus_register_name",
  "horus_lookup_name",
  "horus_set_display_name",
];

export default function GenerateKeyModal({ show, onClose, onKeyCreated }) {
  const { network } = useAuth();
  const [step, setStep] = useState(1);
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [keyName, setKeyName] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expiry, setExpiry] = useState("");
  const [ipAllowlist, setIpAllowlist] = useState([]);
  const [ipInput, setIpInput] = useState("");
  const [toolRestrictions, setToolRestrictions] = useState([]);
  const [rateLimit, setRateLimit] = useState(60);
  const [walletPassword, setWalletPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setStep(1);
      setKeyName("");
      setSelectedWallet(null);
      setShowAdvanced(false);
      setExpiry("");
      setIpAllowlist([]);
      setIpInput("");
      setToolRestrictions([]);
      setRateLimit(60);
      setWalletPassword("");
      loadWallets();
    }
  }, [show]);

  const loadWallets = async () => {
    try {
      const data = await listWalletsForKey();
      // MCP only supports EVM wallets
      const evmWallets = (data.wallets || []).filter(
        (w) => w.network === "EVM"
      );
      setWallets(evmWallets);
      if (evmWallets.length) setSelectedWallet(evmWallets[0]);
    } catch (err) {
      toast.error("Failed to load wallets");
    }
  };

  const handleAddIp = () => {
    const ip = ipInput.trim();
    if (ip && !ipAllowlist.includes(ip)) {
      setIpAllowlist([...ipAllowlist, ip]);
      setIpInput("");
    }
  };

  const handleRemoveIp = (ip) => {
    setIpAllowlist(ipAllowlist.filter((i) => i !== ip));
  };

  const toggleTool = (tool) => {
    setToolRestrictions((prev) =>
      prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]
    );
  };

  const handleGenerate = async () => {
    if (!selectedWallet) {
      toast.error("Please select a wallet");
      return;
    }
    if (!keyName.trim()) {
      toast.error("Please enter a key name");
      return;
    }

    setLoading(true);
    try {
      const params = {
        wallet_index: selectedWallet.walletIndex,
        name: keyName.trim(),
        network: network?.chain || "BASE",
      };

      if (selectedWallet.hasPassword && walletPassword) {
        params.password = walletPassword;
      }

      if (showAdvanced) {
        if (expiry) params.expiresAt = expiry;
        if (ipAllowlist.length) params.ipAllowlist = ipAllowlist;
        if (toolRestrictions.length) params.toolRestrictions = toolRestrictions;
        if (rateLimit !== 60) params.rateLimit = rateLimit;
      }

      const result = await generateMcpKey(params);
      onKeyCreated({
        ...result,
        name: keyName.trim(),
        wallet: selectedWallet,
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to generate key");
    } finally {
      setLoading(false);
    }
  };

  const canProceed =
    step === 1
      ? !!selectedWallet
      : step === 2
      ? !!keyName.trim() && (!selectedWallet?.hasPassword || !!walletPassword)
      : true;

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      id="generate-key-modal"
      className="mcp-modal"
    >
      <Modal.Header>
        <Modal.Title>Generate API Key</Modal.Title>
        <CloseButton
          className="btn-close-white"
          onClick={onClose}
          style={{ position: "absolute", right: "20px", top: "20px" }}
        />
      </Modal.Header>
      <Modal.Body className="mcp-modal-body">
        {/* Step indicators */}
        <div className="mcp-steps">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`mcp-step-dot ${step >= s ? "mcp-step-active" : ""}`}
            >
              {s}
            </div>
          ))}
        </div>

        {/* Step 1: Select wallet */}
        {step === 1 && (
          <div className="mcp-step-content">
            <h5 className="mcp-step-title">Select wallet</h5>
            <p className="mcp-step-desc">
              Choose which wallet this API key will control.
            </p>
            <div className="mcp-wallet-list">
              {wallets.map((w) => (
                <button
                  key={w.walletIndex}
                  type="button"
                  className={`mcp-wallet-option ${
                    selectedWallet?.walletIndex === w.walletIndex
                      ? "mcp-wallet-selected"
                      : ""
                  }`}
                  onClick={() => setSelectedWallet(w)}
                >
                  <span className="mcp-wallet-name">Wallet #{w.walletIndex}</span>
                  <span className="mcp-wallet-addr">
                    ({truncateAddress(w.address)})
                  </span>
                </button>
              ))}
              {wallets.length === 0 && (
                <p className="mcp-muted">Loading wallets...</p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Name the key */}
        {step === 2 && (
          <div className="mcp-step-content">
            <h5 className="mcp-step-title">Name your key</h5>
            <p className="mcp-step-desc">
              Give this key a descriptive name so you can identify it later.
            </p>
            <input
              type="text"
              className="mcp-text-input"
              placeholder="Claude Desktop, Cursor, Work laptop..."
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              autoFocus
            />

            {/* Wallet password (required for password-protected wallets) */}
            {selectedWallet?.hasPassword && (
              <div className="mcp-field" style={{ marginTop: "1rem" }}>
                <label className="mcp-field-label">Wallet password</label>
                <input
                  type="password"
                  className="mcp-text-input"
                  placeholder="Enter your wallet password"
                  value={walletPassword}
                  onChange={(e) => setWalletPassword(e.target.value)}
                />
                <span className="mcp-field-hint">
                  Required for signing transactions. Encrypted and stored securely with the API key.
                </span>
              </div>
            )}

            {/* Advanced restrictions */}
            <button
              type="button"
              className="mcp-advanced-toggle"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? "▾" : "▸"} Advanced security
            </button>

            {showAdvanced && (
              <div className="mcp-advanced-section">
                {/* Expiry */}
                <div className="mcp-field">
                  <label className="mcp-field-label">Expiry</label>
                  <input
                    type="date"
                    className="mcp-text-input"
                    value={expiry}
                    onChange={(e) => setExpiry(e.target.value)}
                    placeholder="Never (default)"
                  />
                  <span className="mcp-field-hint">
                    Leave empty for no expiry
                  </span>
                </div>

                {/* IP Allowlist */}
                <div className="mcp-field">
                  <label className="mcp-field-label">IP allowlist</label>
                  <div className="mcp-ip-input-row">
                    <input
                      type="text"
                      className="mcp-text-input"
                      value={ipInput}
                      onChange={(e) => setIpInput(e.target.value)}
                      placeholder="e.g. 192.168.1.1"
                      onKeyDown={(e) => e.key === "Enter" && handleAddIp()}
                    />
                    <button
                      type="button"
                      className="mcp-btn mcp-btn-sm"
                      onClick={handleAddIp}
                    >
                      Add
                    </button>
                  </div>
                  {ipAllowlist.length > 0 && (
                    <div className="mcp-chip-list">
                      {ipAllowlist.map((ip) => (
                        <span key={ip} className="mcp-chip">
                          {ip}
                          <button
                            type="button"
                            onClick={() => handleRemoveIp(ip)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tool restrictions */}
                <div className="mcp-field">
                  <label className="mcp-field-label">Tool restrictions</label>
                  <div className="mcp-tool-grid">
                    {MCP_TOOLS.map((tool) => (
                      <label key={tool} className="mcp-tool-checkbox">
                        <input
                          type="checkbox"
                          checked={toolRestrictions.includes(tool)}
                          onChange={() => toggleTool(tool)}
                        />
                        <span className="mcp-tool-name">{tool}</span>
                      </label>
                    ))}
                  </div>
                  <span className="mcp-field-hint">
                    Leave all unchecked to allow all tools
                  </span>
                </div>

                {/* Rate limit */}
                <div className="mcp-field">
                  <label className="mcp-field-label">
                    Rate limit (requests/min)
                  </label>
                  <input
                    type="number"
                    className="mcp-text-input mcp-input-sm"
                    value={rateLimit}
                    onChange={(e) => setRateLimit(Number(e.target.value))}
                    min={1}
                    max={1000}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div className="mcp-step-content">
            <h5 className="mcp-step-title">Confirm</h5>
            <div className="mcp-confirm-details">
              <div className="mcp-confirm-row">
                <span className="mcp-confirm-label">Key name</span>
                <span className="mcp-confirm-value">{keyName}</span>
              </div>
              <div className="mcp-confirm-row">
                <span className="mcp-confirm-label">Wallet</span>
                <span className="mcp-confirm-value">
                  #{selectedWallet?.walletIndex} (
                  {truncateAddress(selectedWallet?.address)})
                </span>
              </div>
              {showAdvanced && expiry && (
                <div className="mcp-confirm-row">
                  <span className="mcp-confirm-label">Expires</span>
                  <span className="mcp-confirm-value">{expiry}</span>
                </div>
              )}
              {showAdvanced && ipAllowlist.length > 0 && (
                <div className="mcp-confirm-row">
                  <span className="mcp-confirm-label">IP allowlist</span>
                  <span className="mcp-confirm-value">
                    {ipAllowlist.join(", ")}
                  </span>
                </div>
              )}
              {showAdvanced && toolRestrictions.length > 0 && (
                <div className="mcp-confirm-row">
                  <span className="mcp-confirm-label">Tools</span>
                  <span className="mcp-confirm-value">
                    {toolRestrictions.length} of {MCP_TOOLS.length} allowed
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="mcp-modal-footer">
        {step > 1 && (
          <button
            type="button"
            className="mcp-btn mcp-btn-sm"
            onClick={() => setStep(step - 1)}
          >
            <span>Back</span>
          </button>
        )}
        {step < 3 ? (
          <button
            type="button"
            className="fugu--btn mcp-btn-primary"
            disabled={!canProceed}
            onClick={() => setStep(step + 1)}
          >
            <span>Next</span>
          </button>
        ) : (
          <button
            type="button"
            className="fugu--btn mcp-btn-primary"
            disabled={loading}
            onClick={handleGenerate}
          >
            <span>{loading ? "Generating..." : "Generate key"}</span>
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
